import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useHOA } from '../contexts/HOAContext';
import { clearRecoveryArrival } from '../lib/inviteDetection';
import { InviteStatusCard } from '../components/onboarding/InviteStatusCard';
import { MAX_PASSWORD_BYTES, MIN_PASSWORD_LENGTH } from '../components/onboarding/OnboardingForm';

/**
 * ResetPasswordPage — landing page for Supabase password-recovery emails
 * (resetPasswordForEmail redirectTo: <origin>/reset-password).
 *
 * supabase-js (implicit flow, detectSessionInUrl) turns the
 * #access_token=…&type=recovery fragment into a session and emits
 * PASSWORD_RECOVERY, then strips the fragment. The URL is captured at module
 * load — before that happens — so expired/invalid links (#error_code=otp_expired)
 * can be explained. token_hash (custom email template) and ?code= (PKCE) links
 * are handled too.
 */

const INITIAL_URL = (() => {
  try {
    return {
      search: new URLSearchParams(window.location.search),
      hash: new URLSearchParams(window.location.hash.replace(/^#/, '')),
    };
  } catch (e) {
    return { search: new URLSearchParams(), hash: new URLSearchParams() };
  }
})();

const readInitial = (key) => INITIAL_URL.search.get(key) || INITIAL_URL.hash.get(key) || '';

const byteLength = (value) => {
  try {
    return new TextEncoder().encode(value).length;
  } catch (e) {
    return value.length;
  }
};

const inputClass =
  'w-full px-5 py-3.5 bg-section-bg border border-amber-100/40 focus:border-secondary focus:ring-2 focus:ring-secondary/10 rounded-2xl text-primary-dark placeholder-slate-400 text-base font-medium outline-none transition-all';

const stripAuthParamsFromUrl = () => {
  try {
    window.history.replaceState({}, document.title, window.location.pathname);
  } catch (e) { /* no-op */ }
};

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const { hoa } = useHOA();
  const [view, setView] = useState('checking'); // checking | form | invalid | done
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const resolved = useRef(false);

  useEffect(() => {
    let mounted = true;
    const finish = (next) => {
      if (!mounted || resolved.current) return;
      resolved.current = true;
      setView(next);
    };

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' && session) finish('form');
    });

    const resolveSession = async () => {
      if (readInitial('error') || readInitial('error_code') || readInitial('error_description')) {
        stripAuthParamsFromUrl();
        finish('invalid');
        return;
      }

      try {
        const tokenHash = INITIAL_URL.search.get('token_hash');
        const code = INITIAL_URL.search.get('code');
        if (tokenHash && readInitial('type') === 'recovery') {
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: 'recovery',
          });
          stripAuthParamsFromUrl();
          finish(error || !data?.session ? 'invalid' : 'form');
          return;
        }
        if (code) {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          stripAuthParamsFromUrl();
          finish(error || !data?.session ? 'invalid' : 'form');
          return;
        }

        // Fragment links: getSession() waits for supabase-js to finish
        // processing the URL, so a valid recovery link has a session here.
        // (A recovery link that first landed on "/" is forwarded here with
        // its session already established.)
        const { data } = await supabase.auth.getSession();
        finish(data?.session ? 'form' : 'invalid');
      } catch (e) {
        finish('invalid');
      }
    };
    resolveSession();

    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe();
    };
  }, []);

  const errors = useMemo(() => {
    const errs = {};
    if (password.length < MIN_PASSWORD_LENGTH) {
      errs.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters`;
    } else if (byteLength(password) > MAX_PASSWORD_BYTES) {
      errs.password = 'Password is too long — please use a shorter one';
    }
    if (!confirmPassword || confirmPassword !== password) {
      errs.confirmPassword = "Passwords don't match";
    }
    return errs;
  }, [password, confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ password: true, confirmPassword: true });
    if (Object.keys(errors).length > 0 || submitting) return;

    setSubmitting(true);
    setSubmitError('');
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        const message = (error.message || '').toLowerCase();
        if (message.includes('different from the old')) {
          throw new Error('Please choose a password you have not used for this account before.');
        }
        if (message.includes('session') || message.includes('jwt') || error.status === 401) {
          setView('invalid');
          return;
        }
        throw new Error(error.message || 'Your password could not be updated. Please try again.');
      }
      clearRecoveryArrival();
      setView('done');
      // Full reload so the portal starts from a clean, fully-loaded session.
      window.setTimeout(() => window.location.replace('/portal'), 900);
    } catch (err) {
      setSubmitError(err.message || 'Your password could not be updated. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (view === 'checking') {
    return (
      <main className="min-h-screen bg-main-bg flex items-center justify-center" aria-busy="true">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary" />
        <span className="sr-only">Checking your reset link…</span>
      </main>
    );
  }

  if (view === 'invalid') {
    return (
      <InviteStatusCard
        icon="link_off"
        title="This Reset Link Has Expired"
        body="For your security, password reset links work once and only for a short time. Request a new link and use it right away."
        actions={[
          { label: 'Request a New Link', to: '/?reset=1', variant: 'primary' },
          { label: 'Back to Sign In', to: '/?login=1', variant: 'link' },
        ]}
        footer={hoa?.name || null}
      />
    );
  }

  if (view === 'done') {
    return (
      <InviteStatusCard
        icon="check_circle"
        title="Password Updated"
        body="You're all set. Taking you to your portal…"
      />
    );
  }

  const showError = (field) => touched[field] && errors[field];

  return (
    <main className="min-h-screen bg-main-bg flex items-center justify-center px-4 sm:px-6 py-12">
      <section className="w-full max-w-lg bg-section-bg p-3 sm:p-6 rounded-[2.5rem]">
        <form
          onSubmit={handleSubmit}
          noValidate
          className="bg-white rounded-2xl border border-amber-100/30 shadow-warm p-6 sm:p-10 space-y-6"
        >
          <div className="text-center">
            <div className="w-16 h-16 bg-section-bg rounded-2xl flex items-center justify-center mx-auto mb-5 warm-tactile-shadow">
              <span className="material-symbols-outlined text-secondary" style={{ fontSize: '32px' }} aria-hidden="true">
                lock_reset
              </span>
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-primary-dark">Choose a New Password</h1>
            <p className="text-sm font-medium text-slate-500 mt-2 leading-relaxed">
              Pick something you'll remember. You'll use it every time you sign in.
            </p>
          </div>

          <div>
            <label
              htmlFor="reset-password"
              className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2"
            >
              New Password
            </label>
            <div className="relative">
              <input
                id="reset-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                className={`${inputClass} pr-14`}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                aria-invalid={Boolean(showError('password'))}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-primary-dark transition-colors rounded-full"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>
                  {showPassword ? 'visibility_off' : 'visibility'}
                </span>
              </button>
            </div>
            {showError('password') && (
              <p className="text-red-600 text-xs font-medium mt-1.5" role="alert">{errors.password}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="reset-password-confirm"
              className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2"
            >
              Confirm New Password
            </label>
            <input
              id="reset-password-confirm"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, confirmPassword: true }))}
              className={inputClass}
              placeholder="Type it again"
              autoComplete="new-password"
              aria-invalid={Boolean(showError('confirmPassword'))}
            />
            {showError('confirmPassword') && (
              <p className="text-red-600 text-xs font-medium mt-1.5" role="alert">{errors.confirmPassword}</p>
            )}
          </div>

          {submitError && (
            <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm font-medium" role="alert">
              {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-secondary text-white px-8 py-4 rounded-full font-bold text-base hover:brightness-110 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
          >
            {submitting ? 'Saving…' : 'Save New Password'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="block mx-auto text-sm font-bold text-secondary hover:underline rounded-full px-4 py-2"
          >
            Cancel
          </button>
        </form>
      </section>
    </main>
  );
};

export default ResetPasswordPage;
